import { Injectable } from '@angular/core';
import {
  App,
  AppType,
  FulfillmentOrderConfigApiService,
  GetFulfillmentOrderConfigRequest,
  GetMultiAppRequest,
  PartnerApiService,
  ReleaseType,
} from '@vendasta/marketplace-apps';
import { MarketplaceAppsApiService } from '@vendasta/marketplace-apps/v1';
import {
  FulfillmentFormSubmissionApiService,
  FulfillmentOrder,
  FulfillmentOrderApiService,
  GetFulfillmentOrdersForSalesOrderRequest,
  UpsertFulfillmentFormSubmissionRequest,
} from '@vendasta/order-fulfillment';
import { CustomerSalesOrdersService, SalesOrdersService } from '@vendasta/sales-orders';
import { ProductInfoInterface } from '@vendasta/store';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { WorkOrderPersona } from '../work-order-landing-page/interfaces';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderFormService {
  constructor(
    private fulfillmentFormSubmissionApiService: FulfillmentFormSubmissionApiService,
    private fulfillmentOrderService: FulfillmentOrderApiService,
    private formConfigService: FulfillmentOrderConfigApiService,
    private marketplaceAppsService: MarketplaceAppsApiService,
    private partnerApiService: PartnerApiService,
    private salesOrderService: SalesOrdersService,
    private customerSalesOrderService: CustomerSalesOrdersService,
  ) {}

  getProductInfo(
    appId: string,
    resellerPartnerId: string,
    marketId: string,
    persona: WorkOrderPersona,
  ): Observable<ProductInfoInterface> {
    if (persona === WorkOrderPersona.VENDOR) {
      return this.marketplaceAppsService
        .getApp({
          appId: appId,
          fieldMask: {
            paths: ['name', 'icon', 'tagLine'],
          },
        })
        .pipe(
          map((resp) => resp?.app),
          map((product) => {
            return {
              productId: appId,
              name: product?.name,
              tagline: product?.tagline,
              icon: product?.icon,
            } as ProductInfoInterface;
          }),
        );
    }

    return this.partnerApiService
      .getMultiApp(
        new GetMultiAppRequest({
          appKeys: [
            {
              appId: appId,
            },
          ],
          partnerId: resellerPartnerId,
          marketId: marketId,
        }),
      )
      .pipe(
        take(1),
        map((response) => response?.apps[0] || ({} as App)),
        map((app) => {
          if (app.appType === AppType.APP_TYPE_APP) {
            return {
              productId: appId,
              name: app?.sharedMarketingInformation?.name,
              tagline: app?.sharedMarketingInformation?.tagline,
              icon: app?.sharedMarketingInformation?.iconUrl,
            } as ProductInfoInterface;
          } else {
            return {
              productId: app?.parentRequirements?.parentDetails?.key?.appId,
              name: app?.sharedMarketingInformation?.name,
              tagline: app?.sharedMarketingInformation?.tagline,
              icon: app?.sharedMarketingInformation?.iconUrl,
              addonInfo: [
                {
                  addonId: appId,
                  name: app?.sharedMarketingInformation?.name,
                },
              ],
            } as ProductInfoInterface;
          }
        }),
      );
  }

  updateFormSubmission(request: UpsertFulfillmentFormSubmissionRequest): Observable<string> {
    return this.fulfillmentFormSubmissionApiService
      .upsertFulfillmentFormSubmission(request)
      .pipe(map((resp) => resp?.version));
  }

  /**
   * Looks up config for a given product and returns if it has forms enabled
   * @param workOrder
   */
  doesWorkOrderHaveForm(appId: string): Observable<boolean> {
    return this.formConfigService
      .getFulfillmentOrderConfig(
        new GetFulfillmentOrderConfigRequest({
          appId,
          projectionFilter: {
            includeFormConfig: true,
          },
        }),
      )
      .pipe(
        map((resp) => {
          return Boolean(resp?.config?.usesFulfillmentForm);
        }),
      );
  }

  private getPreviewOrderActivationProductIds(
    salesOrderId: string,
    businessId: string,
    isSMB: boolean,
  ): Observable<string[]> {
    const activations$ = isSMB
      ? this.customerSalesOrderService.previewOrderActivations(salesOrderId, businessId)
      : this.salesOrderService.previewOrderActivations(salesOrderId, businessId);

    return activations$.pipe(
      map((activations) => {
        const productIds = activations?.map((activation) => activation.productId) || [];
        return productIds.filter((productId, index) => productIds.indexOf(productId) === index);
      }),
      switchMap((productIds) => {
        const configs$ = productIds.map((productId) =>
          this.formConfigService.getFulfillmentOrderConfig(
            new GetFulfillmentOrderConfigRequest({
              appId: productId,
              release: ReleaseType.RELEASE_TYPE_PUBLISHED,
            }),
          ),
        );
        return combineLatest(configs$);
      }),
      map((responses) => {
        const resp = responses
          ?.filter((response) => response?.config?.isEnabled && response?.config?.usesFulfillmentForm)
          .map((response) => response?.config?.appId);
        return resp;
      }),
    );
  }

  getOrdersToRenderFormsFor(
    salesOrderId: string,
    businessId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isSMB = false,
    includeSalesInfo = false,
  ): Observable<FulfillmentOrder[]> {
    return this.fulfillmentOrderService
      .getFulfillmentOrdersForSalesOrder(
        new GetFulfillmentOrdersForSalesOrderRequest({
          salesOrderId,
          businessId: businessId,
          cursor: '',
          pageSize: 100,
          projectionFilter: {
            includeSalesInfo: includeSalesInfo,
            includeAppInfo: true,
            includeBusinessInfo: true,
          },
        }),
      )
      .pipe(map((resp) => resp?.fulfillmentOrders || ([] as FulfillmentOrder[])));
  }
}
